import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Users, UserPlus, UserCog, Shield, Briefcase, User as UserIcon,
  Search, RefreshCw, Edit2, Trash2, Key, CheckCircle2, XCircle,
  AlertCircle, Mail, Phone, Building, Calendar, ChevronDown, Eye, EyeOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, by_role: {}, active: 0, inactive: 0, pending_verification: 0 });
  
  // Pending verifications
  const [pendingUsers, setPendingUsers] = useState([]);
  const [showPendingSection, setShowPendingSection] = useState(true);
  const [verifyingUser, setVerifyingUser] = useState(null);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    role: 'client'
  });
  const [newPassword, setNewPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Define callbacks first (before useEffects)
  // Note: Auth is handled via httpOnly cookies, no need for Authorization headers
  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/admin/users/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [isAuthenticated]);

  const fetchPendingVerifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/admin/users/pending-verification`);
      setPendingUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch pending verifications:', error);
    }
  }, [isAuthenticated]);

  const filterUsers = useCallback(() => {
    let filtered = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(term) ||
        u.first_name?.toLowerCase().includes(term) ||
        u.last_name?.toLowerCase().includes(term) ||
        u.company?.toLowerCase().includes(term)
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(u => u.is_active === isActive);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Check authorization - wait for auth loading to complete
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user && user.role !== 'admin') {
      navigate('/admin');
      return;
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchUsers();
      fetchStats();
      fetchPendingVerifications();
    }
  }, [isAuthenticated, user, fetchUsers, fetchStats, fetchPendingVerifications]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleVerifyUser = async (userId) => {
    setVerifyingUser(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/verify-email`);
      toast.success(i18n.language === 'nl' ? 'Gebruiker geverifieerd!' : 'User verified successfully!');
      fetchPendingVerifications();
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify user');
    } finally {
      setVerifyingUser(null);
    }
  };

  const handleRejectUser = async (userId) => {
    setVerifyingUser(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}/reject-verification`);
      toast.success(i18n.language === 'nl' ? 'Registratie afgewezen' : 'Registration rejected');
      fetchPendingVerifications();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setVerifyingUser(null);
    }
  };

  const handleCreateUser = async () => {
    setFormLoading(true);
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success(t('users.userCreated'));
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.createError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        company: formData.company,
        phone: formData.phone,
        role: formData.role,
        is_active: selectedUser.is_active
      };
      await axios.patch(`${API}/admin/users/${selectedUser.id}`, updateData);
      toast.success(t('users.userUpdated'));
      setShowEditDialog(false);
      resetForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.updateError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { is_active: !currentStatus });
      toast.success(t(!currentStatus ? 'users.userActivated' : 'users.userDeactivated'));
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.statusError'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setFormLoading(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, 
        { new_password: newPassword }
      );
      toast.success(t('users.passwordReset'));
      setShowResetPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.passwordResetError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      toast.success(t('users.userDeleted'));
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('users.deleteError'));
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      company: '',
      phone: '',
      role: 'client'
    });
    setSelectedUser(null);
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      email: userToEdit.email,
      password: '',
      first_name: userToEdit.first_name,
      last_name: userToEdit.last_name,
      company: userToEdit.company || '',
      phone: userToEdit.phone || '',
      role: userToEdit.role
    });
    setShowEditDialog(true);
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800 border-purple-300',
      employee: 'bg-blue-100 text-blue-800 border-blue-300',
      client: 'bg-green-100 text-green-800 border-green-300',
      guest: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    const icons = {
      admin: Shield,
      employee: Briefcase,
      client: UserIcon,
      guest: UserIcon
    };
    const Icon = icons[role] || UserIcon;
    
    return (
      <Badge className={`${styles[role] || styles.guest} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {role?.charAt(0).toUpperCase() + role?.slice(1)}
      </Badge>
    );
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-steel-50 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-navy-800 mb-2">{t('admin.accessDenied')}</h1>
          <p className="text-steel-600 mb-4">{t('users.onlyAdmins')}</p>
          <Button onClick={() => navigate('/admin')} className="btn-primary">
            {t('users.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-50" data-testid="user-management">
      {/* Header */}
      <div className="bg-navy-800 pt-24 pb-8">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-8 h-8 text-gold" />
                <h1 className="font-outfit text-3xl font-bold text-white">{t('users.title')}</h1>
              </div>
              <p className="text-steel-300">{t('users.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin')}
                className="border-steel-500 text-white hover:bg-navy-700"
              >
                {t('users.backToDashboard')}
              </Button>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                className="btn-accent"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('users.createUser')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-l-4 border-l-navy-600">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-steel-500">{t('users.totalUsers')}</p>
                  <p className="text-2xl font-bold text-navy-800">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-navy-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-steel-500">{t('users.admins')}</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.by_role?.admin || 0}</p>
                </div>
                <Shield className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-steel-500">{t('users.employees')}</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_role?.employee || 0}</p>
                </div>
                <Briefcase className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-steel-500">{t('users.clients')}</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_role?.client || 0}</p>
                </div>
                <UserIcon className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-steel-500">{t('users.active')}</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.active}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Verifications Section */}
        {pendingUsers.length > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50/50">
            <CardHeader className="border-b border-orange-200 cursor-pointer" onClick={() => setShowPendingSection(!showPendingSection)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-orange-800">
                      {i18n.language === 'nl' ? 'In Afwachting van Verificatie' : 'Pending Email Verification'}
                    </CardTitle>
                    <p className="text-sm text-orange-600">
                      {pendingUsers.length} {i18n.language === 'nl' ? 'gebruikers wachten op goedkeuring' : 'users awaiting approval'}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-orange-600 transition-transform ${showPendingSection ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
            <AnimatePresence>
              {showPendingSection && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <CardContent className="p-0">
                    <div className="divide-y divide-orange-100">
                      {pendingUsers.map((pendingUser) => (
                        <div key={pendingUser.id} className="p-4 flex items-center justify-between hover:bg-orange-100/50">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                              <UserIcon className="w-5 h-5 text-orange-700" />
                            </div>
                            <div>
                              <p className="font-medium text-navy-800">
                                {pendingUser.first_name} {pendingUser.last_name}
                              </p>
                              <div className="flex items-center gap-3 text-sm text-steel-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {pendingUser.email}
                                </span>
                                {pendingUser.company && (
                                  <span className="flex items-center gap-1">
                                    <Building className="w-3 h-3" />
                                    {pendingUser.company}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(pendingUser.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleVerifyUser(pendingUser.id)}
                              disabled={verifyingUser === pendingUser.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {verifyingUser === pendingUser.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  {i18n.language === 'nl' ? 'Goedkeuren' : 'Approve'}
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectUser(pendingUser.id)}
                              disabled={verifyingUser === pendingUser.id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              {i18n.language === 'nl' ? 'Afwijzen' : 'Reject'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                <Input
                  placeholder={t('users.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('users.filterByRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('users.allRoles')}</SelectItem>
                  <SelectItem value="admin">{t('users.roleAdmin')}</SelectItem>
                  <SelectItem value="employee">{t('users.roleEmployee')}</SelectItem>
                  <SelectItem value="client">{t('users.roleClient')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('users.filterByStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('users.allStatus')}</SelectItem>
                  <SelectItem value="active">{t('users.active')}</SelectItem>
                  <SelectItem value="inactive">{t('users.inactive')}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchUsers}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader className="border-b">
            <CardTitle>{t('users.usersList')} ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-gold animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                <p className="text-steel-500">{t('users.noUsers')}</p>
              </div>
            ) : (
              <div className="divide-y divide-steel-100">
                {filteredUsers.map((userItem) => (
                  <motion.div
                    key={userItem.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-steel-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        userItem.role === 'admin' ? 'bg-purple-500' :
                        userItem.role === 'employee' ? 'bg-blue-500' :
                        'bg-green-500'
                      }`}>
                        {userItem.first_name?.[0]}{userItem.last_name?.[0]}
                      </div>

                      {/* Main Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-navy-800">
                            {userItem.first_name} {userItem.last_name}
                          </span>
                          {!userItem.is_active && (
                            <Badge className="bg-red-100 text-red-800 text-xs">{t('users.inactive')}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-steel-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {userItem.email}
                          </span>
                          {userItem.company && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {userItem.company}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Role */}
                      <div>
                        {getRoleBadge(userItem.role)}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(userItem)}
                          className="text-steel-600 hover:text-navy-800"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(userItem);
                            setShowResetPasswordDialog(true);
                          }}
                          className="text-steel-600 hover:text-navy-800"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(userItem.id, userItem.is_active)}
                          className={userItem.is_active ? 'text-amber-600 hover:text-amber-800' : 'text-green-600 hover:text-green-800'}
                        >
                          {userItem.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                        </Button>
                        {userItem.email !== 'info@elementarycargo.com' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(userItem);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-gold" />
              {t('users.createNewUser')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('register.firstName')} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>{t('register.lastName')} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <Label>{t('common.email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <Label>{t('register.password')} *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder={t('users.enterPassword')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({...formData, password: generatePassword()})}
                >
                  {t('users.generate')}
                </Button>
              </div>
            </div>
            <div>
              <Label>{t('users.role')} *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-500" />
                      {t('users.roleAdminFull')}
                    </div>
                  </SelectItem>
                  <SelectItem value="employee">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-500" />
                      {t('users.roleEmployeeFull')}
                    </div>
                  </SelectItem>
                  <SelectItem value="client">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-green-500" />
                      {t('users.roleClientFull')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('register.company')}</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder={t('users.companyPlaceholder')}
              />
            </div>
            <div>
              <Label>{t('common.phone')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={formLoading || !formData.email || !formData.password || !formData.first_name || !formData.last_name}
              className="btn-accent"
            >
              {formLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : t('users.createUser')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-gold" />
              {t('users.editUser')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('register.firstName')} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('register.lastName')} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>{t('common.email')}</Label>
              <Input value={formData.email} disabled className="bg-steel-100" />
            </div>
            <div>
              <Label>{t('users.role')} *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({...formData, role: v})}
                disabled={selectedUser?.email === 'info@elementarycargo.com'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('users.roleAdmin')}</SelectItem>
                  <SelectItem value="employee">{t('users.roleEmployee')}</SelectItem>
                  <SelectItem value="client">{t('users.roleClient')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('register.company')}</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
              />
            </div>
            <div>
              <Label>{t('common.phone')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateUser} disabled={formLoading} className="btn-accent">
              {formLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-gold" />
              {t('users.resetPassword')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-steel-600 mb-4">
              {t('users.setNewPasswordFor')} <strong>{selectedUser?.email}</strong>
            </p>
            <Label>{t('users.newPassword')} *</Label>
            <div className="flex gap-2 mt-2">
              <div className="relative flex-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('users.enterNewPassword')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNewPassword(generatePassword())}
              >
                {t('users.generate')}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetPasswordDialog(false);
              setNewPassword('');
            }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleResetPassword} disabled={formLoading || !newPassword} className="btn-accent">
              {formLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : t('users.resetPassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.confirmDelete')} <strong>{selectedUser?.email}</strong>? 
              {t('users.cannotUndo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
